import { Link, usePage } from "@inertiajs/react";
import {
	Check,
	ChevronDown,
	Copy,
	Home,
	RotateCw,
	TriangleAlert,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
import type { GlobalPageProps } from "@/types/globals";

const COPY_FEEDBACK_MS = 2000;

const CONTENT: Record<number, { title: string; description: string }> = {
	403: {
		title: "Forbidden",
		description: "Sorry, you are forbidden from accessing this page.",
	},
	404: {
		title: "Page Not Found",
		description: "Sorry, the page you are looking for could not be found.",
	},
	500: {
		title: "Server Error",
		description: "Whoops, something went wrong on our servers.",
	},
	503: {
		title: "Service Unavailable",
		description:
			"Sorry, we are doing some maintenance. Please check back soon.",
	},
};

export interface ErrorDetails {
	name: string;
	message: string;
	cause: string | null;
}

export interface ErrorPageProps {
	status: number;
	errorReference?: string;
	errorDetails?: ErrorDetails | null;
}

export default function ErrorPage({
	status,
	errorReference,
	errorDetails,
}: ErrorPageProps) {
	const { props: globalProps } = usePage<GlobalPageProps>();
	const { title, description } = CONTENT[status] ?? {
		title: "Unexpected error",
		description: "Something went wrong while handling your request.",
	};

	// A 5xx may be transient (timeout, deploy, dropped connection); a 404/403
	// never is, so offering "Try again" there would only waste the user's time.
	const isRetryable = status >= 500;
	const homePath =
		globalProps.adminPortal?.router?.authenticatedRootPath ?? "/";

	const [copied, setCopied] = useState(false);
	const copyResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	useEffect(
		() => () => {
			if (copyResetTimer.current) clearTimeout(copyResetTimer.current);
		},
		[],
	);
	const copyReference = useCallback(async () => {
		if (!errorReference) return;

		// Rejects in insecure contexts (plain HTTP) or when permission is denied.
		// The reference stays selectable on the page, so failing quietly is fine —
		// what is not fine is an unhandled rejection on the error page itself.
		try {
			await navigator.clipboard.writeText(errorReference);
			setCopied(true);
			if (copyResetTimer.current) clearTimeout(copyResetTimer.current);
			copyResetTimer.current = setTimeout(
				() => setCopied(false),
				COPY_FEEDBACK_MS,
			);
		} catch {
			setCopied(false);
		}
	}, [errorReference]);

	return (
		<main className="flex items-center justify-center min-h-screen p-4 bg-muted/40">
			<Card className="w-full max-w-xl">
				<CardHeader className="items-center text-center">
					<div className="flex items-center justify-center mb-2 rounded-full size-12 bg-destructive/10">
						<TriangleAlert className="size-6 text-destructive" />
					</div>

					<CardTitle className="text-2xl font-bold tracking-tight">
						{title}
					</CardTitle>

					<CardDescription className="text-base">{description}</CardDescription>

					<span className="font-mono text-xs tracking-widest uppercase text-muted-foreground">
						Error {status}
					</span>
				</CardHeader>

				{(errorReference || errorDetails) && (
					<CardContent className="space-y-4">
						<Separator />

						{errorReference && (
							<div className="space-y-1.5">
								<p className="text-sm font-medium">Reference code</p>

								<div className="flex items-center gap-2">
									<code className="flex-1 px-3 py-2 overflow-x-auto font-mono text-xs rounded-md bg-muted text-muted-foreground">
										{errorReference}
									</code>

									<Button
										type="button"
										variant="outline"
										size="icon"
										onClick={copyReference}
										aria-label="Copy reference code"
									>
										{copied ? (
											<Check className="size-4 text-emerald-600" />
										) : (
											<Copy className="size-4" />
										)}
									</Button>
								</div>

								<p className="text-xs text-muted-foreground">
									Please include this code when reporting the problem — it lets
									us find the exact request in the server logs.
								</p>
							</div>
						)}

						{errorDetails && (
							<Collapsible>
								<CollapsibleTrigger className="flex items-center w-full gap-1.5 text-sm font-medium group text-muted-foreground hover:text-foreground">
									<ChevronDown className="size-4 transition-transform group-data-[state=open]:rotate-180" />
									Technical details
								</CollapsibleTrigger>

								<CollapsibleContent className="pt-2 space-y-2">
									<div className="p-3 space-y-2 rounded-md bg-muted">
										<p className="font-mono text-xs font-medium break-words">
											{errorDetails.name}
										</p>

										<p className="font-mono text-xs break-words text-muted-foreground">
											{errorDetails.message}
										</p>

										{errorDetails.cause && (
											<p className="font-mono text-xs break-words text-muted-foreground">
												Caused by: {errorDetails.cause}
											</p>
										)}
									</div>
								</CollapsibleContent>
							</Collapsible>
						)}
					</CardContent>
				)}

				<CardFooter className="flex flex-col gap-2 sm:flex-row sm:justify-center">
					{isRetryable && (
						<Button
							type="button"
							variant="outline"
							className="w-full sm:w-auto"
							onClick={() => window.location.reload()}
						>
							<RotateCw className="mr-2 size-4" />
							Try again
						</Button>
					)}

					<Button asChild className="w-full sm:w-auto">
						<Link href={homePath}>
							<Home className="mr-2 size-4" />
							Back to home
						</Link>
					</Button>
				</CardFooter>
			</Card>
		</main>
	);
}
